import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Heart, MessageCircle, Star, Send } from 'lucide-react';
import Navbar from './Navbar';

const API_BASE_URL = 'https://api.4fingerstudios.com';

const ComicDetail = () => {
  const { comicName } = useParams();
  const navigate = useNavigate();
  const [comic, setComic] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Like, Rating, and Comment states
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [averageRating, setAverageRating] = useState(0);
  const [userRating, setUserRating] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const fetchComicData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Convert URL-friendly name back to original format if needed
      const formattedName = comicName.replace(/-/g, ' ');
      
      // Try to fetch comic data from API
      // Adjust the endpoint based on your API structure
      const response = await fetch(`${API_BASE_URL}/api/comics/${encodeURIComponent(formattedName)}`);
      
      if (response.ok) {
        const data = await response.json();
        setComic(data);
        setPages(data.pages || data.images || []);
      } else if (response.status === 404) {
        // If API doesn't have the comic, use mock data for now
        // You can replace this with actual API calls when ready
        setComic({
          title: formattedName,
          date: 'Coming Soon',
          description: 'Comic description here'
        });
        // Mock pages - replace with actual image URLs
        setPages([
          // Add your image URLs here
          // Example: 'https://cdn.4fingerstudios.com/comics/comic-name/page1.jpg'
        ]);
      } else {
        throw new Error('Failed to fetch comic data');
      }
    } catch (err) {
      console.error('Error fetching comic:', err);
      setError('Failed to load comic. Please try again later.');
      // Set fallback data
      setComic({
        title: comicName.replace(/-/g, ' '),
        date: 'Coming Soon',
        description: 'Comic description here'
      });
      setPages([]);
    } finally {
      setIsLoading(false);
    }
  }, [comicName]);

  const fetchInteractions = useCallback(async () => {
    try {
      const formattedName = comicName.replace(/-/g, ' ');
      const storageKey = `comic_${comicName}_interactions`;
      
      // Try API first
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/comics/${encodeURIComponent(formattedName)}/interactions`, {
          headers
        });
        
        if (response.ok) {
          const data = await response.json();
          setLikes(data.likes || 0);
          setIsLiked(data.isLiked || false);
          setAverageRating(data.averageRating || 0);
          setUserRating(data.userRating || 0);
          setComments(data.comments || []);
          return;
        }
      } catch (apiError) {
        console.log('API fetch failed, using localStorage');
      }
      
      // Fallback to localStorage
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        setLikes(data.likes || 0);
        setIsLiked(data.isLiked || false);
        setAverageRating(data.averageRating || 0);
        setUserRating(data.userRating || 0);
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Error fetching interactions:', err);
    }
  }, [comicName]);

  useEffect(() => {
    checkAuth();
    fetchComicData();
    fetchInteractions();
  }, [comicName, fetchComicData, fetchInteractions]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const userData = await response.json();
          setIsAuthenticated(true);
          setCurrentUser(userData);
        } else {
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    } else {
      setIsAuthenticated(false);
      setCurrentUser(null);
    }
  };


  const handleLike = async () => {
    const newLikedState = !isLiked;
    const newLikes = newLikedState ? likes + 1 : Math.max(0, likes - 1);
    
    setIsLiked(newLikedState);
    setLikes(newLikes);
    
    try {
      const formattedName = comicName.replace(/-/g, ' ');
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      
      try {
        await fetch(`${API_BASE_URL}/api/comics/${encodeURIComponent(formattedName)}/like`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ liked: newLikedState })
        });
      } catch (apiError) {
        // Fallback to localStorage
        const storageKey = `comic_${comicName}_interactions`;
        const stored = localStorage.getItem(storageKey);
        const data = stored ? JSON.parse(stored) : { likes: 0, isLiked: false, averageRating: 0, userRating: 0, comments: [] };
        data.likes = newLikes;
        data.isLiked = newLikedState;
        localStorage.setItem(storageKey, JSON.stringify(data));
      }
    } catch (err) {
      console.error('Error updating like:', err);
      // Revert on error
      setIsLiked(!newLikedState);
      setLikes(likes);
    }
  };

  const handleRating = async (rating) => {
    if (rating < 1 || rating > 5) return;
    
    const oldRating = userRating;
    setUserRating(rating);
    
    try {
      const formattedName = comicName.replace(/-/g, ' ');
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/comics/${encodeURIComponent(formattedName)}/rate`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ rating })
        });
        
        if (response.ok) {
          const data = await response.json();
          setAverageRating(data.averageRating || 0);
        }
      } catch (apiError) {
        // Fallback to localStorage
        const storageKey = `comic_${comicName}_interactions`;
        const stored = localStorage.getItem(storageKey);
        const data = stored ? JSON.parse(stored) : { likes: 0, isLiked: false, averageRating: 0, userRating: 0, comments: [] };
        data.userRating = rating;
        
        // Calculate average (simplified - in real app, this would be server-side)
        const allRatings = data.ratings || [];
        if (oldRating > 0) {
          const index = allRatings.findIndex(r => r.userId === (currentUser?.id || 'anonymous'));
          if (index >= 0) allRatings[index] = { userId: currentUser?.id || 'anonymous', rating };
          else allRatings.push({ userId: currentUser?.id || 'anonymous', rating });
        } else {
          allRatings.push({ userId: currentUser?.id || 'anonymous', rating });
        }
        data.ratings = allRatings;
        const sum = allRatings.reduce((acc, r) => acc + r.rating, 0);
        data.averageRating = allRatings.length > 0 ? (sum / allRatings.length).toFixed(1) : 0;
        setAverageRating(parseFloat(data.averageRating));
        
        localStorage.setItem(storageKey, JSON.stringify(data));
      }
    } catch (err) {
      console.error('Error updating rating:', err);
      setUserRating(oldRating);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || isSubmittingComment) return;
    
    setIsSubmittingComment(true);
    const newComment = {
      id: Date.now(),
      text: commentText.trim(),
      author: currentUser?.username || 'Anonymous',
      timestamp: new Date().toISOString(),
      date: new Date().toLocaleDateString()
    };
    
    const updatedComments = [newComment, ...comments];
    setComments(updatedComments);
    setCommentText('');
    
    try {
      const formattedName = comicName.replace(/-/g, ' ');
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      
      try {
        await fetch(`${API_BASE_URL}/api/comics/${encodeURIComponent(formattedName)}/comments`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ text: newComment.text })
        });
      } catch (apiError) {
        // Fallback to localStorage
        const storageKey = `comic_${comicName}_interactions`;
        const stored = localStorage.getItem(storageKey);
        const data = stored ? JSON.parse(stored) : { likes: 0, isLiked: false, averageRating: 0, userRating: 0, comments: [] };
        data.comments = updatedComments;
        localStorage.setItem(storageKey, JSON.stringify(data));
      }
    } catch (err) {
      console.error('Error submitting comment:', err);
      // Revert on error
      setComments(comments);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const goToPreviousPage = useCallback(() => {
    setCurrentPageIndex((prev) => {
      if (prev > 0) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return prev - 1;
      }
      return prev;
    });
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPageIndex((prev) => {
      if (prev < pages.length - 1) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return prev + 1;
      }
      return prev;
    });
  }, [pages.length]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'ArrowLeft') goToPreviousPage();
    if (e.key === 'ArrowRight') goToNextPage();
  }, [goToPreviousPage, goToNextPage]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white font-sans">
        <Navbar currentPage="Comic" />
        <div className="flex items-center justify-center min-h-screen pt-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mx-auto mb-4"></div>
            <p className="text-red-400/70">Loading comic...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !comic) {
    return (
      <div className="min-h-screen bg-black text-white font-sans">
        <Navbar currentPage="Comic" />
        <div className="flex items-center justify-center min-h-screen pt-32">
          <div className="text-center">
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-red-500 hover:bg-red-600 rounded-lg transition-colors duration-300"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      <Navbar currentPage="Comic" />
      
      {/* Header Section */}
      <section className="relative pt-32 pb-12 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Animated Background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-black"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(239,68,68,0.15),transparent_50%)]"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(147,51,234,0.1),transparent_50%)]"></div>
          </div>

          {/* Back Button */}
          <div className="relative z-10 mb-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-red-400/70 hover:text-red-400 transition-colors duration-300 group"
            >
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform duration-300" />
              <span className="font-medium">Back to Comics</span>
            </button>
          </div>

          {/* Comic Title */}
          <div className="relative z-10 text-center mb-8">
            <h1 className="text-4xl md:text-6xl font-black mb-4 tracking-tight bg-gradient-to-r from-red-500 to-purple-500 bg-clip-text text-transparent">
              {comic?.title || comicName.replace(/-/g, ' ')}
            </h1>
            {comic?.date && (
              <p className="text-red-400/70 text-lg">Date: {comic.date}</p>
            )}
            {comic?.description && (
              <p className="text-white/60 max-w-2xl mx-auto mt-4">{comic.description}</p>
            )}
          </div>
        </div>
      </section>

      {/* Comic Pages Section */}
      <section className="py-12 px-6 relative">
        <div className="max-w-5xl mx-auto">
          {pages.length === 0 ? (
            <div className="text-center py-24">
              <div className="bg-black/80 backdrop-blur-sm border border-red-500/20 rounded-2xl p-12">
                <p className="text-6xl mb-6">📚</p>
                <h3 className="text-2xl font-bold mb-4 text-white">No pages available yet</h3>
                <p className="text-red-400/70">Check back soon for comic pages!</p>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Page Navigation */}
              {pages.length > 1 && (
                <div className="flex items-center justify-between bg-black/80 backdrop-blur-sm border border-red-500/20 rounded-xl p-4">
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPageIndex === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all duration-300 disabled:hover:bg-red-500/20"
                  >
                    <ChevronLeft size={20} />
                    <span>Previous</span>
                  </button>
                  
                  <span className="text-white/70 font-medium">
                    Page {currentPageIndex + 1} of {pages.length}
                  </span>
                  
                  <button
                    onClick={goToNextPage}
                    disabled={currentPageIndex === pages.length - 1}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all duration-300 disabled:hover:bg-red-500/20"
                  >
                    <span>Next</span>
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}

              {/* Current Page Display */}
              <div className="bg-black/80 backdrop-blur-sm border border-red-500/20 rounded-2xl overflow-hidden">
                <div className="flex justify-center items-center min-h-[600px] p-6">
                  {pages[currentPageIndex] && (
                    <img
                      src={pages[currentPageIndex]}
                      alt={`Page ${currentPageIndex + 1}`}
                      className="max-w-full h-auto rounded-lg shadow-2xl"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%231a1a1a" width="800" height="600"/%3E%3Ctext fill="%23ef4444" font-family="Arial" font-size="24" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage not available%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Page Thumbnails (if multiple pages) */}
              {pages.length > 1 && (
                <div className="bg-black/80 backdrop-blur-sm border border-red-500/20 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 text-center text-white">All Pages</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {pages.map((page, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setCurrentPageIndex(index);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`relative overflow-hidden rounded-lg border-2 transition-all duration-300 ${
                          index === currentPageIndex
                            ? 'border-red-500 shadow-lg shadow-red-500/50 scale-105'
                            : 'border-red-500/20 hover:border-red-500/40 hover:scale-102'
                        }`}
                      >
                        <img
                          src={page}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-auto object-cover"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect fill="%231a1a1a" width="200" height="150"/%3E%3Ctext fill="%23ef4444" font-family="Arial" font-size="12" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EPage%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs py-1 text-center">
                          {index + 1}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Interactions Section - Like, Rating, Comments */}
      <section className="py-12 px-6 relative">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Like and Rating Section */}
          <div className="bg-black/80 backdrop-blur-sm border border-red-500/20 rounded-2xl p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Like Button */}
              <button
                onClick={handleLike}
                className={`flex items-center gap-3 px-6 py-3 rounded-xl transition-all duration-300 ${
                  isLiked
                    ? 'bg-red-500/20 text-red-400 border-2 border-red-500/50 hover:bg-red-500/30'
                    : 'bg-black/40 text-white/70 border-2 border-red-500/20 hover:border-red-500/40 hover:text-red-400'
                }`}
              >
                <Heart size={24} className={isLiked ? 'fill-red-500' : ''} />
                <span className="font-bold text-lg">{likes}</span>
                <span className="text-sm">{isLiked ? 'Liked' : 'Like'}</span>
              </button>

              {/* Rating Section */}
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-white/70 font-medium">Rate this comic:</span>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => handleRating(star)}
                        className="transition-all duration-200 hover:scale-110"
                        type="button"
                      >
                        <Star
                          size={28}
                          className={
                            star <= userRating
                              ? 'fill-yellow-400 text-yellow-400'
                              : star <= averageRating
                              ? 'fill-yellow-400/30 text-yellow-400/30'
                              : 'text-white/30'
                          }
                        />
                      </button>
                    ))}
                  </div>
                </div>
                {averageRating > 0 && (
                  <p className="text-sm text-white/60">
                    Average: <span className="text-yellow-400 font-bold">{averageRating.toFixed(1)}</span> / 5.0
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="bg-black/80 backdrop-blur-sm border border-red-500/20 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle size={24} className="text-red-400" />
              <h2 className="text-2xl font-bold text-white">Comments</h2>
              <span className="text-white/60 text-sm">({comments.length})</span>
            </div>

            {/* Comment Form */}
            <form onSubmit={handleSubmitComment} className="mb-6">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={isAuthenticated ? "Share your thoughts about this comic..." : "Please log in to comment"}
                disabled={!isAuthenticated || isSubmittingComment}
                className="w-full bg-black/60 border border-red-500/20 rounded-lg p-4 text-white placeholder-red-400/50 focus:border-red-500/40 focus:outline-none transition-all duration-300 resize-none min-h-[100px] disabled:opacity-50 disabled:cursor-not-allowed"
                rows="4"
              />
              <div className="flex justify-end mt-3">
                <button
                  type="submit"
                  disabled={!commentText.trim() || !isAuthenticated || isSubmittingComment}
                  className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-red-500 to-purple-500 text-white font-bold rounded-lg hover:from-red-600 hover:to-purple-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
                >
                  {isSubmittingComment ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      <span>Posting...</span>
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      <span>Post Comment</span>
                    </>
                  )}
                </button>
              </div>
              {!isAuthenticated && (
                <p className="text-sm text-red-400/70 mt-2">
                  Please log in to leave a comment
                </p>
              )}
            </form>

            {/* Comments List */}
            <div className="space-y-4">
              {comments.length === 0 ? (
                <div className="text-center py-8 text-white/60">
                  <MessageCircle size={48} className="mx-auto mb-3 opacity-30" />
                  <p>No comments yet. Be the first to share your thoughts!</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="bg-black/40 border border-red-500/10 rounded-lg p-4 hover:border-red-500/20 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {comment.author.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-bold text-white">{comment.author}</span>
                      </div>
                      <span className="text-xs text-white/50">{comment.date}</span>
                    </div>
                    <p className="text-white/80 leading-relaxed">{comment.text}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComicDetail;
